import { useCallback, useEffect, useState } from 'react';
import type { Variables } from '../../NarrativeCommunityList/hooks/useNarrativesCommunityInfinite';
import { usePathname } from '@/hooks/location/usePathname';

type SortField = NonNullable<Variables['sort']>['field'];
type SortDirection = NonNullable<Variables['sort']>['direction'];

export const SortFieldMap = {
  undervalued: {
    field: 'INTRINSIC_DISCOUNT',
    direction: 'DESC',
  },
  overvalued: {
    field: 'INTRINSIC_DISCOUNT',
    direction: 'ASC',
  },
  popular: {
    field: 'POPULARITY',
    direction: 'DESC',
  },
} as const satisfies Record<
  string,
  { field: SortField; direction: SortDirection }
>;

type SortHash = keyof typeof SortFieldMap;

const getCurrentSortHash = () => {
  const hash = window.location.hash;
  if (!hash) return null;
  const sortHash = hash.slice(1);
  return sortHash && sortHash in SortFieldMap ? (sortHash as SortHash) : null;
};

const changeSortHash = (newSort: SortHash | null) => {
  const previousScrollY = window.scrollY;
  if (newSort) {
    window.location.hash = newSort;
  } else {
    window.location.hash = '';
    const { origin, pathname, search } = window.location;
    const url = new URL(pathname, origin);
    const params = new URLSearchParams(search);

    const updatedParams =
      params.toString() !== '' ? `?${params.toString()}` : '';

    const targetUrl = `${url}${updatedParams}`;

    if (targetUrl === window.location.href) return;

    history.replaceState(null, '', targetUrl);
    window.scrollTo({
      top: previousScrollY,
      behavior: 'auto',
    });
  }
};

export const useNarrativeCommunitySort = () => {
  // This state only exists so that we re-render when the hash changes
  const [sortHash, setSortHash] = useState<SortHash | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    const handleHashChange = () => setSortHash(getCurrentSortHash());

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  useEffect(() => {
    setSortHash(getCurrentSortHash());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const forwardSortHash = useCallback(
    (pathnameAndSearch: string) => {
      return `${pathnameAndSearch}${sortHash ? `#${sortHash}` : ''}`;
    },
    [sortHash]
  );

  return {
    sort: sortHash ? SortFieldMap[sortHash] : null,
    sortHash,
    changeSort: changeSortHash,
    forwardSortHash,
  };
};
